// CHANGE v2 §7: renomeado de NetWorth para Património.
// O backend mantém net_worth_eur / net_worth_change_eur técnicos — só copy e rota renomeiam.
import { Suspense, lazy, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Camera, ChartBar } from '@phosphor-icons/react';

import { NewSelfieButton } from '../../components/snapshots/NewSelfieButton';
import { EmptyState } from '../../components/common/EmptyState';
import { PageContentShell } from '../../components/common/PageContentShell';
import { Button } from '../../components/ui/Button';
import { Skeleton } from '../../components/ui/Skeleton';
import { PatrimonioHeroCard } from '../../components/patrimonio/PatrimonioHeroCard';
import { PatrimonioFormulaCard } from '../../components/patrimonio/PatrimonioFormulaCard';
import { PatrimonioCompositionSection } from '../../components/patrimonio/PatrimonioCompositionSection';
import {
  buildPatrimonioComposition,
  computeGrowthStreak,
  formatSelfieShortDate,
} from '../../lib/patrimonioComposition';
import { useActiveAccountsQuery } from '../../features/accounts/queries';
import {
  useCurrentSnapshotQuery,
  useSnapshotsTimelineQuery,
} from '../../features/snapshots/queries';
import { useSelfieCycle } from '../../store/SelfieCycleContext';
import { useQueryShellState } from '../../hooks/useQueryShellState';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { cycleDaysRemaining } from '../../utils/cycleDays';
import { formatEUR } from '../../utils/money';
import { toNumber } from '../../utils/number';
import { cn } from '../../utils/cn';
import { paths } from '../../routes/paths';
import pt from '../../locales/pt';

const PatrimonioJourneyChart = lazy(() =>
  import('../../components/patrimonio/PatrimonioJourneyChart'),
);

function formatSignedEUR(value) {
  const formatted = formatEUR(value);
  if (value > 0) return `+${formatted}`;
  return formatted;
}

function MetricCardSkeleton({ className, children }) {
  return (
    <div
      className={cn(
        'flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-5 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]',
        className,
      )}
      aria-busy="true"
      aria-label={pt.common.loading}
    >
      {children}
    </div>
  );
}

function CompositionRowSkeleton({ b1, b2 }) {
  return (
    <div className="flex items-center gap-space-3 py-space-2">
      <Skeleton className="h-10 w-10 shrink-0 rounded-ds-md" />
      <div className="flex flex-1 flex-col gap-space-2">
        <Skeleton className="h-[11px] rounded-ds-full" style={{ width: b1 }} />
        <Skeleton className="h-[9px] rounded-ds-full" style={{ width: b2 }} />
      </div>
      <Skeleton className="h-3.5 w-20 shrink-0 rounded-ds-full" />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <MetricCardSkeleton>
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-[140px] rounded-ds-full" />
        <Skeleton className="h-5 w-24 rounded-ds-full" />
      </div>
      <Skeleton className="h-[150px] w-full rounded-ds-sm" />
      <div className="flex gap-space-4">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <Skeleton key={i} className="h-2.5 flex-1 rounded-ds-full" />
        ))}
      </div>
    </MetricCardSkeleton>
  );
}

function PatrimonioContentFallback() {
  return (
    <div className="flex flex-col gap-space-5">
      <MetricCardSkeleton>
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-[120px] rounded-ds-full" />
          <Skeleton className="h-5 w-[88px] rounded-ds-full" />
        </div>
        <Skeleton className="h-8 w-[260px] rounded-ds-sm" />
      </MetricCardSkeleton>

      <ChartSkeleton />

      <div className="flex flex-col gap-space-3 rounded-ds-lg border border-border bg-card p-space-4 shadow-[0_6px_16px] shadow-glass-shadow/[0.12] dark:shadow-glass-shadow/[0.45]">
        <div className="flex items-center justify-between">
          <Skeleton className="h-3 w-24 rounded-ds-full" />
          <Skeleton className="h-[18px] w-[110px] rounded-ds-full" />
        </div>
        <div className="h-px w-full bg-border" />
        <CompositionRowSkeleton b1={160} b2={120} />
        <CompositionRowSkeleton b1={150} b2={90} />
        <CompositionRowSkeleton b1={150} b2={110} />
        <CompositionRowSkeleton b1={140} b2={86} />
      </div>
    </div>
  );
}

export function Patrimonio() {
  const isMd = useMediaQuery('(min-width: 768px)');
  const isLg = useMediaQuery('(min-width: 1024px)');
  const layout = isLg ? 'desktop' : isMd ? 'tablet' : 'mobile';

  const {
    data: timelineData,
    isPending: timelinePending,
    isFetching: timelineFetching,
    isError: timelineIsError,
    refetch: refetchTimeline,
  } = useSnapshotsTimelineQuery();
  const timeline = useMemo(() => timelineData ?? [], [timelineData]);
  const {
    data: current,
    isPending: currentPending,
    isFetching: currentFetching,
    isError: currentIsError,
    refetch: refetchCurrent,
  } = useCurrentSnapshotQuery();
  const { data: accounts = [] } = useActiveAccountsQuery();
  const { currentCycle } = useSelfieCycle();

  const timelineShell = useQueryShellState({
    data: timelineData,
    isPending: timelinePending,
    isFetching: timelineFetching,
  });
  const currentShell = useQueryShellState({
    data: current,
    isPending: currentPending,
    isFetching: currentFetching,
  });

  // Empty timeline is valid — don't wait for current snapshot.
  const contentReady =
    !timelineShell.showBlockingSkeleton &&
    (timeline.length === 0 || !currentShell.showBlockingSkeleton);

  const accountIndex = useMemo(() => {
    const map = new Map();
    for (const account of accounts) {
      map.set(account.id, account);
    }
    return map;
  }, [accounts]);

  const growthStreak = useMemo(() => computeGrowthStreak(timeline), [timeline]);
  const daysUntilNextSelfie = useMemo(
    () => cycleDaysRemaining(currentCycle),
    [currentCycle],
  );

  const heroValue = toNumber(current?.net_worth_eur);
  const totalAssets = toNumber(current?.total_assets_eur);
  const totalLiabilities = toNumber(current?.total_liabilities_eur);
  const heroChange =
    timeline.length >= 2 && current?.net_worth_change_eur != null
      ? toNumber(current.net_worth_change_eur)
      : null;

  const composition = useMemo(
    () =>
      buildPatrimonioComposition({
        accountSnapshots: current?.account_snapshots || [],
        accountIndex,
        liabilitySnapshots: current?.liability_snapshots || [],
      }),
    [current, accountIndex],
  );

  const snapshotDateLabel = formatSelfieShortDate(current?.snapshot_date);

  /** `.pen` → bloco `error` de ds/card/networth-readout. */
  const heroIsError = (currentIsError || timelineIsError) && !current;

  const headerSubtitle = useMemo(() => {
    if (!current) return pt.patrimonio.subtitle;
    if (heroChange !== null) {
      return pt.patrimonio.desktopSubtitle
        .replace('{value}', formatEUR(heroValue))
        .replace('{delta}', formatSignedEUR(heroChange));
    }
    return pt.patrimonio.desktopSubtitleValueOnly.replace(
      '{value}',
      formatEUR(heroValue),
    );
  }, [current, heroValue, heroChange]);

  const header = (
    <header className="hidden items-center justify-between gap-space-4 md:flex">
      <div className="flex flex-col gap-1">
        <h1 className="type-title-page">{pt.patrimonio.title}</h1>
        <p className="type-body-sm text-muted-foreground">{headerSubtitle}</p>
      </div>
      <Button asChild variant="ghost" size="sm" className="text-sage-on-tint hover:bg-sage-tint">
        <Link to={paths.selfies}>
          <ChartBar className="h-4 w-4" aria-hidden />
          {pt.patrimonio.viewReports}
        </Link>
      </Button>
    </header>
  );

  const hero = (
    <PatrimonioHeroCard
      value={heroValue}
      change={heroChange}
      snapshotDate={current?.snapshot_date}
      growthStreak={growthStreak}
      isFirstSelfie={timeline.length === 1}
      layout={layout}
      /* `.pen` → bloco `error` de ds/card/networth-readout */
      isError={heroIsError}
      onRetry={() => {
        refetchCurrent();
        refetchTimeline();
      }}
    />
  );

  const journey = (
    <Suspense fallback={<ChartSkeleton />}>
      <PatrimonioJourneyChart
        timeline={timeline}
        daysUntilNextSelfie={daysUntilNextSelfie}
        isLoading={false}
      />
    </Suspense>
  );

  const formulaDensity = layout === 'desktop' ? 'full' : layout === 'tablet' ? 'cozy' : 'compact';

  const formula = current ? (
    <PatrimonioFormulaCard
      assets={totalAssets}
      liabilities={totalLiabilities}
      netWorth={heroValue}
      density={formulaDensity}
    />
  ) : null;

  const compositionSection = current ? (
    <PatrimonioCompositionSection
      assetGroups={composition.assetGroups}
      liabilityItems={composition.liabilityItems}
      totalAssets={composition.totalAssets}
      totalLiabilities={composition.totalLiabilities}
      snapshotDateLabel={snapshotDateLabel}
    />
  ) : null;

  const compositionSectionStacked = current ? (
    <PatrimonioCompositionSection
      assetGroups={composition.assetGroups}
      liabilityItems={composition.liabilityItems}
      totalAssets={composition.totalAssets}
      totalLiabilities={composition.totalLiabilities}
      snapshotDateLabel={snapshotDateLabel}
      stackedLinks
    />
  ) : null;

  let body = null;
  if (heroIsError) {
    /** Erro de carga: o `.pen` mostra o cartão em estado `error`, não o vazio. */
    body = <div className="flex flex-col gap-space-5">{hero}</div>;
  } else if (timeline.length === 0) {
    body = (
      <EmptyState
        icon={Camera}
        title={pt.patrimonio.empty.title}
        description={pt.patrimonio.empty.subtitle}
        action={
          <NewSelfieButton label={pt.patrimonio.empty.cta} variant="default" icon={Camera} />
        }
      />
    );
  } else if (layout === 'desktop') {
    body = (
      <div
        className="grid min-w-0 grid-cols-[1.55fr_1fr] items-start gap-space-5"
        data-testid="patrimonio-layout-desktop"
      >
        <div className="flex min-w-0 flex-col gap-space-5">
          {hero}
          {journey}
          {formula}
        </div>
        <div className="flex min-w-0 flex-col gap-space-5">{compositionSection}</div>
      </div>
    );
  } else if (layout === 'tablet') {
    body = (
      <div className="flex items-start gap-space-5" data-testid="patrimonio-layout-tablet">
        <div className="flex min-w-0 flex-1 flex-col gap-space-5">
          {hero}
          {journey}
          {formula}
        </div>
        <div className="flex min-w-0 flex-1 flex-col gap-space-5">
          {compositionSectionStacked}
        </div>
      </div>
    );
  } else {
    body = (
      <div className="flex flex-col gap-space-5" data-testid="patrimonio-layout-mobile">
        {hero}
        {journey}
        {formula}
        {compositionSection}
      </div>
    );
  }

  return (
    <div className="flex w-full min-w-0 flex-col gap-space-5 overflow-x-hidden">
      {header}
      <PageContentShell
        ready={contentReady}
        fallback={<PatrimonioContentFallback />}
        className="flex flex-col gap-space-5"
      >
        {body}
      </PageContentShell>
    </div>
  );
}
